import Vue from 'vue';

import userReviewsService from '@d-services/user-reviews-service/user-reviews-service';
import { getErrorMessage } from '@d-shared/utils/errors';

async function _getEntries(commit, state) {
  if (state.isLoading || !state.hasMoreResults) {
    return;
  }

  commit('saveIsLoading', true);

  try {
    const results = await userReviewsService.getUserReviewsForBook(
      state.bookSlug,
      state.sortBy,
      state.rating,
      state.nextPage,
    );

    if (!state.isInitialized) {
      commit('setIsInitialized');
    }

    commit('saveEntries', results.user_reviews);
    commit('savePaginationInfo', !!results.meta.next_page);
  } catch (error) {
    Vue.duiNotificationManager.error(getErrorMessage(error));

    if (!state.isInitialized) {
      commit('setIsInitialized');
    }
  }

  commit('saveIsLoading', false);
}

const state = {
  bookSlug: null,
  count: 0,
  entries: [],
  hasMoreResults: true,
  isCountLoaded: false,
  isInitialized: false,
  isLoading: false,
  isUserReviewsListPresent: false,
  nextPage: 1,
  rating: null,
  sortBy: 'created_at',
  sortingOptions: [
    {
      selected: true,
      text: 'Recently added',
      value: 'created_at',
    },
    {
      text: 'Higher scores',
      value: 'higher',
    },
    {
      text: 'Lower scores',
      value: 'lower',
    },
  ],
};

const getters = {
  'get-count': (state) => state.count,
  'get-entries': (state) => state.entries,
  'get-has-more-results': (state) => state.hasMoreResults,
  'get-initialized-state': (state) => state.isInitialized,
  'get-is-user-reviews-list-present': (state) => state.isUserReviewsListPresent,
  'get-loading-state': (state) => state.isLoading,
  'get-sorting-options': (state) => state.sortingOptions,
};

const actions = {
  getEntries({ commit, state }, bookSlug) {
    commit('saveBookSlug', bookSlug);
    _getEntries(commit, state);
  },

  async loadCount({ commit, state }, bookSlug) {
    if (state.isCountLoaded) {
      return;
    }

    commit('setCountLoadedState');

    try {
      const count = await userReviewsService.getUserReviewsCountForBook(bookSlug);
      commit('saveCount', count);
    } catch (error) {
      Vue.duiNotificationManager.error(getErrorMessage(error));
    }
  },

  selectRating({ commit, state }, rating) {
    if (!state.bookSlug) {
      return;
    }

    if (rating === state.rating) {
      rating = null;
    }

    commit('resetEntries');
    commit('saveRating', rating);
    _getEntries(commit, state);
  },

  selectSortingOption({ commit, state }, option) {
    if (!option || (option.value === state.sortBy) || !option.selected) {
      return;
    }

    commit('resetEntries');
    commit('saveSortingOption', option);
    _getEntries(commit, state);
  },

  setUserReviewsListPresence({ commit }) {
    commit('saveUserReviewsListPresence');
  },
};

const mutations = {
  resetEntries(state) {
    Vue.set(state, 'entries', []);
    Vue.set(state, 'hasMoreResults', true);
    Vue.set(state, 'isInitialized', false);
    Vue.set(state, 'nextPage', 1);
    Vue.set(state, 'rating', null);
    Vue.set(state, 'sortBy', 'created_at');
  },

  saveBookSlug(state, bookSlug) {
    Vue.set(state, 'bookSlug', bookSlug);
  },

  saveCount(state, count) {
    Vue.set(state, 'count', count);
  },

  saveEntries(state, entries) {
    Vue.set(state, 'entries', state.entries.concat(entries));
  },

  saveIsLoading(state, isLoading) {
    if (state.isLoading === isLoading) {
      return;
    }

    Vue.set(state, 'isLoading', isLoading);
  },

  savePaginationInfo(state, hasMoreResults) {
    Vue.set(state, 'hasMoreResults', hasMoreResults);
    Vue.set(state, 'nextPage', state.nextPage + 1);
  },

  saveRating(state, rating) {
    Vue.set(state, 'rating', rating);
  },

  saveSortingOption(state, option) {
    Vue.set(state, 'sortBy', option.value);
  },

  saveUserReviewsListPresence(state) {
    Vue.set(state, 'isUserReviewsListPresent', true);
  },

  setCountLoadedState(state) {
    Vue.set(state, 'isCountLoaded', true);
  },

  setIsInitialized(state) {
    Vue.set(state, 'isInitialized', true);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
